import React, { useState, useEffect } from 'react';
import { RefreshCcw, DollarSign, TrendingUp, PieChart, Landmark } from 'lucide-react';
import { fetchCryptoMarketData } from '../../utils/crypto';
import { useCryptoWebSocket } from '../../hooks/useCryptoWebSocket';
import MarketStat from './MarketStat';

export default function MarketStats() {
  const [marketData, setMarketData] = useState<{
    price: number;
    marketCap: number;
    priceChange24h: number;
    dominance: number;
  } | null>(null);
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const { price: wsPrice, isConnected } = useCryptoWebSocket();

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const data = await fetchCryptoMarketData();
      setMarketData(data);
      setError('');
    } catch (error) {
      setError('Unable to fetch market data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (wsPrice && marketData) {
      setMarketData(prev => prev ? { ...prev, price: wsPrice } : prev);
      // Update price change if needed
      const priceChange = ((wsPrice - (marketData?.price || wsPrice)) / (marketData?.price || wsPrice)) * 100;
      if (Math.abs(priceChange) > 0.01) { // Only update if change is significant
        setMarketData(prev => prev ? { ...prev, priceChange24h: prev.priceChange24h + priceChange } : prev);
      }
    }
  }, [wsPrice]);

  if (error) {
    return (
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg p-4">
        <div className="flex items-center justify-between">
          <p className="text-gray-400">{error}</p>
          <button
            onClick={fetchData}
            className="p-2 hover:bg-gray-700 rounded-full transition-colors"
            aria-label="Retry"
          >
            <RefreshCcw className="w-4 h-4 text-purple-400" />
          </button>
        </div>
      </div>
    );
  }

  if (isLoading || !marketData) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-gray-800/50 backdrop-blur-sm rounded-lg p-4">
            <div className="animate-pulse h-12 bg-gray-700 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <MarketStat
        title="Bitcoin Price"
        value={`$${marketData.price.toLocaleString(undefined, { 
          maximumFractionDigits: 2 
        })}`}
        className={isConnected ? 'animate-pulse-subtle' : ''}
        icon={DollarSign}
      />
      <MarketStat
        title="24h Change"
        value={`${marketData.priceChange24h.toFixed(2)}%`}
        icon={TrendingUp}
        className={marketData.priceChange24h >= 0 ? 'text-green-500' : 'text-red-500'}
      />
      <MarketStat
        title="Market Cap"
        value={`$${marketData.marketCap.toFixed(2)}T`}
        icon={Landmark}
      />
      <MarketStat
        title="BTC Dominance"
        value={`${marketData.dominance.toFixed(2)}%`}
        icon={PieChart}
      />
    </div>
  );
}